import { api } from "@/redux/api";

import {
	RunTrackerSignalScanResult,
	RunTrackerSignalScanResultSchema,
} from "@/pages/Private/pages/ProfileTracker/schema/runSignalScan";

import {
	ListSignalConfig,
	ListSignalConfigSchema,
	UpdateSignalConfig,
} from "../../pages/List/schema/list";

import {
	CompanySignalConfig,
	CompanySignalConfigSchema,
	UpdateCompanySignalConfig,
	TestSignalConfigRequest,
	TestSignalConfigResponse,
	TestSignalConfigResponseSchema,
	SignalPriorityHistoryParams,
	SignalPriorityHistoryResponse,
	SignalPriorityHistoryResponseSchema,
} from "../../pages/Signals/schema/signal";

import {
	ProspectSignalResultParams,
	IgnoreTrackerSignalRequest,
	TrackerSignalFeedbackResponse,
	TrackerSignalFeedbackResponseSchema,
	TrackerSignalResultParams,
	TimelineEntryResponse,
	TimelineEntryResponseSchema,
} from "../../pages/ProfileTracker/schema/trackerSignalResult";

export const signalApi = api.injectEndpoints({
	endpoints: (builder) => ({
		// List-level signal config endpoints
		getSignalConfig: builder.query<ListSignalConfig, number>({
			query: (listId) => ({
				url: `signal/${listId}/signal-config`,
				responseSchema: ListSignalConfigSchema,
			}),
			providesTags: ["SignalConfig"],
		}),
		updateSignalConfig: builder.mutation<
			ListSignalConfig,
			{ listId: number; data: UpdateSignalConfig }
		>({
			query: ({ listId, data }) => ({
				url: `signal/${listId}/signal-config`,
				method: "PUT",
				body: data,
				responseSchema: ListSignalConfigSchema,
			}),
			invalidatesTags: (_result, _error, arg) => ["SignalConfig", { type: "List", id: arg.listId }],
		}),

		// Company-level signal config endpoints (template)
		getCompanySignalConfig: builder.query<CompanySignalConfig, void>({
			query: () => ({
				url: `signal/config`,
				responseSchema: CompanySignalConfigSchema,
			}),
			providesTags: ["CompanySignalConfig"],
		}),
		updateCompanySignalConfig: builder.mutation<CompanySignalConfig, UpdateCompanySignalConfig>({
			query: (data) => ({
				url: `signal/config`,
				method: "PUT",
				body: data,
				responseSchema: CompanySignalConfigSchema,
			}),
			invalidatesTags: ["CompanySignalConfig"],
		}),

		// Test signal config (dry run)
		testSignalConfig: builder.mutation<TestSignalConfigResponse, TestSignalConfigRequest>({
			query: (data) => ({
				url: `signal/config/test`,
				method: "POST",
				body: data,
				responseSchema: TestSignalConfigResponseSchema,
			}),
		}),

		// Tracker organization signals endpoint
		getTrackerOrganizationSignals: builder.query<TimelineEntryResponse, TrackerSignalResultParams>({
			query: ({ trackerOrganizationId, ...params }) => ({
				url: `signal/tracker-organization/${trackerOrganizationId}/signals`,
				params: {
					...(params.page && { page: params.page }),
					...(params.limit && { limit: params.limit }),
					...(params.searchValue && { searchValue: params.searchValue }),
					...(params.sortBy && { sortBy: params.sortBy }),
					...(params.sortOrder && { sortOrder: params.sortOrder }),
					...(params.signalFilterStatus && {
						signalFilterStatus: JSON.stringify(params.signalFilterStatus),
					}),
					...(params.appearanceProspectId && {
						appearanceProspectId: params.appearanceProspectId,
					}),
					...(params.includeListAppearances === false && { includeListAppearances: false }),
				},
				responseSchema: TimelineEntryResponseSchema,
			}),
			providesTags: (_result, _error, arg) => [
				{ type: "TrackerSignals", id: arg.trackerOrganizationId },
			],
		}),

		// Signal priority history endpoint
		getSignalPriorityHistory: builder.query<
			SignalPriorityHistoryResponse,
			SignalPriorityHistoryParams
		>({
			query: (params) => ({
				url: "signal/priority-history",
				params: {
					...(params.page && { page: params.page }),
					...(params.limit && { limit: params.limit }),
					...(params.sortBy && { sortBy: params.sortBy }),
					...(params.sortOrder && { sortOrder: params.sortOrder }),
				},
				responseSchema: SignalPriorityHistoryResponseSchema,
			}),
			providesTags: ["SignalPriorityHistory"],
		}),

		// Prospect signals endpoint (for Unassigned view where prospect has no tracker org)
		getProspectSignals: builder.query<TimelineEntryResponse, ProspectSignalResultParams>({
			query: ({ prospectId, ...params }) => ({
				url: `signal/prospect/${prospectId}/signals`,
				params: {
					...(params.page && { page: params.page }),
					...(params.limit && { limit: params.limit }),
					...(params.signalFilterStatus && {
						signalFilterStatus: JSON.stringify(params.signalFilterStatus),
					}),
					...(params.includeListAppearances === false && { includeListAppearances: false }),
				},
				responseSchema: TimelineEntryResponseSchema,
			}),
			providesTags: (_result, _error, arg) => [
				{ type: "TrackerSignals", id: `prospect-${arg.prospectId}` },
			],
		}),

		// "Run signal search" for tracked companies, now (PREC-3240). The cells leave "Looking for
		// signals" when the scan socket reports each finish, so nothing is invalidated here.
		runTrackerSignalScan: builder.mutation<
			RunTrackerSignalScanResult,
			{ trackerId: number; trackerOrganizationIds?: number[]; all?: boolean }
		>({
			query: ({ trackerId, ...body }) => ({
				url: `signal/tracker/${trackerId}/scan`,
				method: "POST",
				body,
				responseSchema: RunTrackerSignalScanResultSchema,
			}),
		}),

		ignoreTrackerSignal: builder.mutation<
			TrackerSignalFeedbackResponse,
			{ signalId: number; data: IgnoreTrackerSignalRequest }
		>({
			query: ({ signalId, data }) => ({
				url: `signal/tracker-signal-results/${signalId}/feedback`,
				method: "POST",
				body: data,
				responseSchema: TrackerSignalFeedbackResponseSchema,
			}),
			invalidatesTags: ["TrackerSignals"],
		}),
	}),
});

export const {
	useGetSignalConfigQuery,
	useUpdateSignalConfigMutation,
	useGetCompanySignalConfigQuery,
	useUpdateCompanySignalConfigMutation,
	useTestSignalConfigMutation,
	useGetTrackerOrganizationSignalsQuery,
	useLazyGetTrackerOrganizationSignalsQuery,
	useGetProspectSignalsQuery,
	useLazyGetProspectSignalsQuery,
	useIgnoreTrackerSignalMutation,
	useRunTrackerSignalScanMutation,
	useGetSignalPriorityHistoryQuery,
	useLazyGetSignalPriorityHistoryQuery,
} = signalApi;
